import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getExpenseDashboardSummary,
  getCategoryTrends,
  getRecentTransactions,
  getEntryHeatmap,
  getTopSpenders,
  getDepartmentCategory,
  getLocationDistribution,
  getExpenseApprovalPipeline,
  getBudgetUsage,
  getExpenseRiskSignals,
  getProjectDashboard,
} from '../../../services/expenseDashboardService';
import { getDepartments } from '../../../services/departmentService';
import { getLocations } from '../../../services/locationService';

const SLICES = {
  summary: getExpenseDashboardSummary,
  categories: getCategoryTrends,
  recent: getRecentTransactions,
  heatmap: getEntryHeatmap,
  topSpenders: getTopSpenders,
  deptCategory: getDepartmentCategory,
  locations: getLocationDistribution,
  pipeline: getExpenseApprovalPipeline,
  budget: getBudgetUsage,
  risks: getExpenseRiskSignals,
  projects: getProjectDashboard,
};

const initialState = Object.keys(SLICES).reduce((acc, key) => {
  acc[key] = { data: null, loading: true };
  return acc;
}, {});

export const useExpenseDashboardApi = () => {
  const [filters, setFilters] = useState({ departmentId: null, locationId: null });
  const [slices, setSlices] = useState(initialState);
  const [departments, setDepartments] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);

  const load = useCallback(async (activeFilters) => {
    setSlices((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => { next[key] = { ...next[key], loading: true }; });
      return next;
    });

    await Promise.all(Object.entries(SLICES).map(async ([key, fetcher]) => {
      try {
        const res = await fetcher(activeFilters);
        setSlices((prev) => ({ ...prev, [key]: { data: res.data?.data ?? null, loading: false } }));
      } catch {
        setSlices((prev) => ({ ...prev, [key]: { data: null, loading: false } }));
      }
    }));
  }, []);

  useEffect(() => {
    load(filters);
  }, [filters, load]);

  useEffect(() => {
    (async () => {
      try {
        const res = await getDepartments({ pageSize: 200, orderBy: 'name' });
        setDepartments(res.data?.data?.data ?? []);
      } catch { /* interceptor */ }
      try {
        const res = await getLocations({ pageSize: 200, orderBy: 'name' });
        setLocationOptions(res.data?.data?.data ?? []);
      } catch { /* interceptor */ }
    })();
  }, []);

  const refetchAll = useCallback(() => load(filters), [load, filters]);

  // Görünen tüm kategoriler → stabil renk haritası girdisi
  const categories = useMemo(() => {
    const seen = new Map();
    (slices.categories.data?.monthlyMatrix || []).forEach((r) => seen.set(r.categoryId, { id: r.categoryId, name: r.categoryName }));
    (slices.deptCategory.data?.rows || []).forEach((r) => seen.set(r.categoryId, { id: r.categoryId, name: r.categoryName }));
    (slices.projects.data?.categoryMix || []).forEach((r) => seen.set(r.categoryId, { id: r.categoryId, name: r.categoryName }));
    return [...seen.values()];
  }, [slices.categories.data, slices.deptCategory.data, slices.projects.data]);

  return {
    filters,
    setFilters,
    slices,
    departments,
    locationOptions,
    categories,
    refetchAll,
  };
};
